import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {CSSTransition} from 'react-transition-group'
import {toast} from 'react-toastify'
import {useFormik} from 'formik'
import {ConfirmActionSwal} from '../../../../../../_gori/partials/widgets'
import {IdentityVerificationModal} from '../../../../auth/Modal/IdentityVerificationModal'
import SettingsService from '../../../core/_requests'
import {VerificationPhoneModal} from '../../../../auth/Modal/VerificationPhoneModal'
import {getCountryFlagFromPhone} from '../../../helpers/phoneHelpers'
import {KTSVG} from '../../../../../../_gori/helpers'

interface PhoneDetailViewProps {
  phoneSetting: any
  currentUser: any
  onBack: () => void
  onReloadSettings: () => void
}

export const PhoneDetailView: React.FC<PhoneDetailViewProps> = ({
  phoneSetting,
  currentUser,
  onBack,
  onReloadSettings,
}) => {
  const intl = useIntl()
  const [localPhoneSetting, setLocalPhoneSetting] = useState(phoneSetting)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [phoneToDelete, setPhoneToDelete] = useState('')
  const [verifiedPhone, setVerifiedPhone] = useState(false)
  const [errorMessageVerifiedPhone, setErrorMessageVerifiedPhone] = useState(false)
  const [showModal, setShowModal] = useState({
    verify_phone: false,
    identity_verification: false,
  })
  const [dataModal, setDataModal] = useState<{
    verify_phone: {whitelisted?: boolean} | null
    identity_verification: any
  }>({
    verify_phone: null,
    identity_verification: null,
  })
  const [selectedDefaultPhone, setSelectedDefaultPhone] = useState('')
  const [initialDefaultPhone, setInitialDefaultPhone] = useState('')
  const [addPhoneLoading, setAddPhoneLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>(
    localPhoneSetting?.config?.numbers || []
  )

  const mountedRef = useRef(true)
  useEffect(() => {
    return () => {
      mountedRef.current = false
    }
  }, [])

  const formik = useFormik({
    initialValues: {
      phone: '',
    },
    onSubmit: async () => {},
  })

  const defaultPhone = localPhoneSetting?.config?.default || ''
  const fallbackPhone = currentUser?.phone || ''
  const hasSettings = !!localPhoneSetting && phoneNumbers.length > 0

  // Show fallback phone ONLY when no record exists in DB at all
  const showFallbackPhone = !localPhoneSetting && !!fallbackPhone

  React.useEffect(() => {
    if (defaultPhone) {
      setInitialDefaultPhone(defaultPhone)
      setSelectedDefaultPhone(defaultPhone)
    }
  }, [defaultPhone])

  const handleOpenAddPhoneModal = () => {
    formik.resetForm()
    setVerifiedPhone(false)
    setErrorMessageVerifiedPhone(false)
    setShowModal((prev) => ({...prev, identity_verification: true}))
  }

  const handlePhoneVerified = useCallback(async () => {
    setVerifiedPhone(false)
    try {
      setAddPhoneLoading(true)
      await new Promise((resolve) => setTimeout(resolve, 1500))
      if (!mountedRef.current) return
      setShowModal({
        verify_phone: false,
        identity_verification: false,
      })
      const response = await SettingsService.addPhoneNumber({
        phone_number: formik.values.phone,
      })

      if (!mountedRef.current) return
      // Update local state immediately for instant UI update
      if (response?.setting) {
        setLocalPhoneSetting(response.setting)
        setPhoneNumbers(response.setting.config?.numbers || [])
      }

      toast.success(intl.formatMessage({id: 'PHONE_NUMBER_ADDED_SUCCESSFULLY'}))
      onReloadSettings()
    } catch (error: any) {
      if (!mountedRef.current) return
      console.error('Error adding phone number:', error)
      toast.error(
        intl.formatMessage({id: error?.response?.data?.error?.message || 'ERROR_OCCURRED'})
      )
      setShowModal({
        verify_phone: false,
        identity_verification: false,
      })
    } finally {
      if (mountedRef.current) {
        setAddPhoneLoading(false)
      }
    }
  }, [formik.values.phone, intl, onReloadSettings])

  React.useEffect(() => {
    if (verifiedPhone && formik.values.phone) {
      handlePhoneVerified()
    }
  }, [verifiedPhone, handlePhoneVerified, formik.values.phone])

  const handleDeleteClick = (phone: string) => {
    setPhoneToDelete(phone)
    setShowDeleteModal(true)
  }

  const handleCloseModal = () => {
    setShowDeleteModal(false)
    setPhoneToDelete('')
  }

  const handleConfirmDelete = async () => {
    if (!phoneToDelete) return

    try {
      setAddPhoneLoading(true)
      const response = await SettingsService.removePhoneNumber({
        phone_number: phoneToDelete,
      })

      // Update local state immediately for instant UI update
      if (response?.setting) {
        setLocalPhoneSetting(response.setting)
        setPhoneNumbers(response.setting.config?.numbers || [])
      }

      onReloadSettings()
      toast.success(intl.formatMessage({id: 'PHONE_NUMBER_REMOVED_SUCCESSFULLY'}))
    } catch (error) {
      console.error('Error deleting phone number:', error)
      toast.error(intl.formatMessage({id: 'ERROR_OCCURRED'}))
    } finally {
      setAddPhoneLoading(false)
    }
  }

  const handleDefaultPhoneChange = async (phone: string) => {
    if (!phone || phone === initialDefaultPhone) return

    try {
      setUpdateLoading(true)
      await SettingsService.setDefaultPhoneNumber({
        phone_number: phone,
      })

      setInitialDefaultPhone(phone)

      toast.success(intl.formatMessage({id: 'UPDATED_SUCCESSFULLY'}))

      onReloadSettings()
    } catch (error) {
      console.error('Error updating default phone:', error)
      if (error instanceof Error) {
        toast.error(intl.formatMessage({id: error.message}))
      } else {
        toast.error(intl.formatMessage({id: 'ERROR_OCCURRED'}))
      }
    } finally {
      setUpdateLoading(false)
    }
  }

  return (
    <>
      {/* Remove Phone Confirm Dialog */}
      {showDeleteModal && (
        <ConfirmActionSwal
          show={showDeleteModal}
          title={intl.formatMessage({id: 'REMOVE_PHONE_NUMBER'})}
          message={`${intl.formatMessage({
            id: 'REMOVE_PHONE_CONFIRM_MESSAGE_1',
          })}<br/>${intl.formatMessage({id: 'REMOVE_PHONE_CONFIRM_MESSAGE_2'})}`}
          messageCancel={intl.formatMessage({id: 'CANCEL'})}
          confirmButtonText='CONFIRM'
          handleCallBack={handleConfirmDelete}
          handleClose={handleCloseModal}
        />
      )}

      {showModal.verify_phone && (
        <VerificationPhoneModal
          show={showModal.verify_phone}
          data={dataModal.verify_phone}
          handleClose={() => {
            setShowModal((prev) => ({...prev, verify_phone: false}))
          }}
          phone={formik.values.phone}
          fromPage='settings_phone_2fa'
          verifiedPhone={verifiedPhone}
          updateVerifiedPhone={() => setVerifiedPhone(true)}
          triggerSubmitForm={() => {}}
        />
      )}

      {/* Identity Verification Modal */}
      {showModal.identity_verification && (
        <IdentityVerificationModal
          show={showModal.identity_verification}
          handleClose={() => {
            setShowModal((prev) => ({...prev, identity_verification: false}))
          }}
          formik={formik}
          checkChangePhone={(value) => formik.setFieldValue('phone', value)}
          setVerifiedPhone={setVerifiedPhone}
          errorMessageVerifiedPhone={errorMessageVerifiedPhone}
          setErrorMessageVerifiedPhone={setErrorMessageVerifiedPhone}
          setShowModal={setShowModal}
          setDataModal={setDataModal}
          title={'ADD_2FA_PHONE'}
        />
      )}

      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        <div className='mb-4'>
          <div className='d-flex flex-column align-items-center justify-content-center'>
            <div className='d-flex justify-content-start w-100 settings-header'>
              <h1 className='d-flex flex-column text-dark fw-boldest my-0 fs-1'>
                {intl.formatMessage({id: 'SECURITY'})}
              </h1>
            </div>
            <div className='card settings-card w-100 border border-secondary rounded-3 py-6 px-8 mt-6'>
              {/* Title with Back Arrow */}
              <div className='d-flex align-items-center mb-3'>
                <i
                  className='bi bi-chevron-left fs-4 text-gray-600 me-2 cursor-pointer'
                  onClick={onBack}
                ></i>
                <h4 className='fw-bold mb-0 ms-2'>
                  {intl.formatMessage({id: 'TWO_FACTOR_AUTHENTICATION_PHONE'})}
                </h4>
              </div>

              {/* Description */}
              <div className='ms-xxl-5'>
                <p className='text-gray-800 mb-5 ms-xxl-5'>
                  {intl.formatMessage({id: 'PHONE_2FA_DESCRIPTION'})}
                </p>
                <br />

                {addPhoneLoading ? (
                  <p className='card-text placeholder-glow ms-xxl-5'>
                    <span className='placeholder col-7 mb-3 placeholder-lg rounded-2 bg-secondary'></span>
                    <span className='placeholder col-4 mb-3 placeholder-lg rounded-2 bg-secondary'></span>
                    <span className='placeholder col-4 mb-3 placeholder-lg rounded-2 bg-secondary'></span>
                    <span className='placeholder col-6 mb-3 placeholder-lg rounded-2 bg-secondary'></span>
                    <span className='placeholder col-8 mb-3 placeholder-lg rounded-2 bg-secondary'></span>
                  </p>
                ) : (
                  <>
                    {/* Default Section - Only show when 2 phones */}
                    {phoneNumbers.length === 2 && (
                      <h5 className='fw-bold mb-4 ms-xxl-5'>
                        {intl.formatMessage({id: 'DEFAULT'})}
                      </h5>
                    )}

                    {/* Scenario 1: No settings and no fallback phone - Only show Add button */}
                    {!hasSettings && !showFallbackPhone && (
                      <button
                        className='btn btn-link text-primary p-0 ms-xxl-5'
                        onClick={handleOpenAddPhoneModal}
                      >
                        {intl.formatMessage({id: 'ADD_2FA_VIA_PHONE'})}
                      </button>
                    )}

                    {/* Scenario 2: No DB record but has fallback phone */}
                    {showFallbackPhone && (
                      <>
                        <div className='d-flex align-items-center justify-content-between mb-4 pb-4 border-bottom ms-xxl-5'>
                          <div className='d-flex align-items-center'>
                            <KTSVG
                              path={getCountryFlagFromPhone(fallbackPhone)}
                              className='me-3'
                              svgClassName='rounded-circle border'
                              small={false}
                              style={{width: '24px', height: '24px'}}
                            />
                            <span className='text-gray-800'>{fallbackPhone}</span>
                          </div>
                          <i
                            className='text-gray-600 fs-6 cursor-pointer'
                            onClick={() => handleDeleteClick(fallbackPhone)}
                          >
                            <KTSVG path='/media/gori/orders/trash.svg' className={'svg-icon-1'} />
                          </i>
                        </div>
                        <button
                          className='btn btn-link text-primary p-0 ms-xxl-5 text-decoration-underline'
                          onClick={handleOpenAddPhoneModal}
                        >
                          {intl.formatMessage({id: 'ADD_BACKUP_2FA_VIA_PHONE'})}
                        </button>
                      </>
                    )}

                    {/* Scenario 3a: Has 1 phone from settings */}
                    {phoneNumbers.length === 1 && (
                      <>
                        <div className='d-flex align-items-center justify-content-between mb-4 pb-4 border-bottom ms-xxl-5'>
                          <div className='d-flex align-items-center'>
                            <KTSVG
                              path={getCountryFlagFromPhone(phoneNumbers[0])}
                              className='me-3'
                              svgClassName='rounded-circle border'
                              small={false}
                              style={{width: '24px', height: '24px'}}
                            />
                            <span className='text-gray-800 fs-6'>{phoneNumbers[0]}</span>
                          </div>
                          <i
                            className='text-gray-600 cursor-pointer'
                            onClick={() => handleDeleteClick(phoneNumbers[0])}
                          >
                            <KTSVG path='/media/gori/orders/trash.svg' className={'svg-icon-1'} />
                          </i>
                        </div>
                        <button
                          className='btn btn-link text-primary p-0 ms-xxl-5 text-decoration-underline'
                          onClick={handleOpenAddPhoneModal}
                        >
                          {intl.formatMessage({id: 'ADD_BACKUP_2FA_VIA_PHONE'})}
                        </button>
                      </>
                    )}

                    {/* Scenario 3b: Has 2 phones from settings */}
                    {phoneNumbers.length === 2 && (
                      <>
                        {phoneNumbers.map((phone, index) => (
                          <div
                            key={phone}
                            className={`d-flex align-items-center justify-content-between mb-4 ms-xxl-5 ${
                              index === 0 ? 'pb-4 border-bottom' : ''
                            }`}
                          >
                            <div className='d-flex align-items-center'>
                              <div className='form-check me-3'>
                                <input
                                  type='radio'
                                  className='form-check-input'
                                  name='default_phone'
                                  checked={phone === selectedDefaultPhone}
                                  disabled={updateLoading}
                                  onChange={() => {
                                    setSelectedDefaultPhone(phone)
                                    handleDefaultPhoneChange(phone)
                                  }}
                                />
                              </div>
                              <KTSVG
                                path={getCountryFlagFromPhone(phone)}
                                className='me-5'
                                svgClassName='rounded-circle border'
                                style={{width: '24px', height: '24px'}}
                              />
                              <span className='text-gray-800 fs-6'>{phone}</span>
                            </div>
                            <i
                              className='text-gray-600 fs-6 cursor-pointer'
                              onClick={() => handleDeleteClick(phone)}
                            >
                              <KTSVG path='/media/gori/orders/trash.svg' className={'svg-icon-1'} />
                            </i>
                          </div>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </CSSTransition>
    </>
  )
}
